/**
 * The Inspector: whatever is in hand or was last called, told plainly.
 *
 * It renders an InspectorModel: facts, facets (with the caveat when the API
 * trusts something it does not keep, like a claim's rank), the verbs the
 * thing takes part in, and the call itself four ways: REST, curl, MCP and
 * the answer. It never shows a key: REST and MCP say `sbk_…••••`, curl says
 * `$SB_KEY`, and every string passes the Wire's redactor on the way in.
 *
 * The model is CORE's (model/inspect.ts): a bit names the occupier that
 * holds it and its role, a call its refusal in the API's words. This screen
 * only draws it.
 *
 * `ctx.inspect(subject)` reaches this screen over the bus (`inspect`).
 */

import { h, mount as put } from '../../../console/src/dom.ts';
import type {
  Answered, AppContext, CallRecord, InspectorModel, Screen, Subject, TargetKind, Twins, Unsub, Verb,
} from '../contracts.ts';
import { inspect } from '../model/inspect.ts';
import { answerOf, knownSecrets, pretty, redactAuthorization, redactJson, redactText, redactTwins } from './wire-log.ts';

// -- the four views of a call ------------------------------------------------------------------

export interface TwinTexts { readonly rest: string; readonly curl: string; readonly mcp: string | null }

/** REST as a request you could read aloud, curl as a command, MCP as the JSON-RPC envelope; no key in any. */
export const twinTexts = (twins: Twins, known: readonly (string | null | undefined)[] = []): TwinTexts => {
  const t = redactTwins(twins, known);
  const headers = Object.entries(t.rest.headers).map(([k, v]) => `${k}: ${k.toLowerCase() === 'authorization' ? redactAuthorization(v) : v}`);
  const rest = [`${t.rest.method} ${t.rest.url}`, ...headers, ...(t.rest.body === undefined ? [] : ['', pretty(t.rest.body)])].join('\n');
  return { rest, curl: t.curl, mcp: t.mcp === null ? null : pretty(t.mcp) };
};

const kOf = (n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n));

/**
 * One line of verdict, then the body: `201 · 43 ms · x-journal-head eade3b… · 297/300`. A call
 * sent as MCP tools/call says the operation's own status first, and the envelope's after it.
 */
export const answerLine = (a: Answered, via: CallRecord['via'] = 'rest'): string => {
  const v = answerOf(a, via);
  const parts = [a.status === 0 ? 'unreachable' : v.rpc ? v.detail : String(v.status), `${a.ms} ms`];
  if (v.mcp !== null) parts.push(`via MCP ${v.mcp}`);
  if (a.head) parts.push(`x-journal-head ${a.head.slice(0, 8)}…`);
  if (a.rate) parts.push(`${a.rate.remaining}/${kOf(a.rate.limit)}`);
  if (a.requestId) parts.push(`request ${a.requestId.slice(0, 12)}`);
  return parts.join(' · ');
};

export const answerText = (a: Answered, known: readonly (string | null | undefined)[] = [], via: CallRecord['via'] = 'rest'): string =>
  `${answerLine(a, via)}\n\n${pretty(redactJson(a.body, known))}`;

// -- where a verb is dropped, in words --------------------------------------------------------

const TARGET_WORD: Partial<Record<TargetKind, string>> = {
  'gate:void': 'Void', 'gate:door': 'Door', 'gate:seal': 'Seal', 'gate:queue': 'Queue', 'gate:sync': 'Sync', 'gate:lay': 'Lay',
  'gate:accept': 'Accept', 'gate:reject': 'Reject', 'gate:return': 'Return', 'gate:turbine': 'Turbine', 'gate:mock': 'Mock turbine',
  'gate:portal': 'Portal', 'gate:docs': 'Docs', 'gate:mcp': 'MCP gate', 'gate:signup': 'Sign up', 'gate:socket': 'Socket',
  'gate:verify': 'Verify', 'gate:launch': 'Launch well', 'gate:mint': 'Mint', 'gate:director': 'Director',
  orb: 'onto an orb', ring: 'onto a World', row: 'onto a row', bits: 'onto bits', field: 'onto the field', tick: 'onto a tick',
  roster: 'onto the roster', vault: 'into the Vault', rack: 'onto the rack', band: 'onto the band', sigil: 'onto the sigil',
  prompt: 'onto the prompt', inbox: 'into the inbox', header: 'onto the header', ground: 'onto the ground', river: 'onto the River',
};
export const targetWord = (k: TargetKind): string => TARGET_WORD[k] ?? k;

/** "release (Void)" — the operation first, then where it is dropped. */
export const verbWord = (v: Verb): string => `${v.op} (${targetWord(v.target)})`;

// -- the view: everything as text, scrubbed ----------------------------------------------------

export type Tab = 'rest' | 'curl' | 'mcp' | 'answer';
export interface InspectorView {
  readonly eyebrow: string;
  readonly title: string;
  readonly facts: readonly (readonly [string, string])[];
  readonly facets: readonly { label: string; value: string; caveat?: string }[];
  readonly verbs: readonly { label: string; verb: Verb }[];
  readonly tabs: readonly { key: Tab; label: string; text: string | null }[];
  readonly tone: 'ok' | 'refused' | 'error' | 'pending' | 'none';
}

/**
 * The lines above a call's curl and MCP forms that make them runnable elsewhere: where $SB_API and $SB_KEY come
 * from (and $ANTHROPIC_API_KEY, for a call that carries the user's own), and where the MCP body is posted. Never
 * a key itself.
 */
export const setupLines = (apiBase: string, anthropic = false): { curl: string; mcp: string } => ({
  curl: `# once, in your shell: export SB_API=${apiBase} SB_KEY=<your sbk key>${anthropic ? ' ANTHROPIC_API_KEY=<your sk-ant key>' : ''}   (MCP lens · Connect an outside client)`,
  mcp: 'POST $SB_API/v1/mcp\nauthorization: Bearer $SB_KEY\ncontent-type: application/json\n',
});

export const viewOf = (m: InspectorModel, known: readonly (string | null | undefined)[] = [], env?: { readonly apiBase: string }): InspectorView => {
  const texts = m.twins ? twinTexts(m.twins, known) : null;
  const via = m.subject.kind === 'call' ? m.subject.record.via : 'rest';
  const answer = m.answer ? answerOf(m.answer, via) : null;
  const setup = env ? setupLines(env.apiBase, texts?.curl.includes('$ANTHROPIC_API_KEY') ?? false) : null;
  return {
    eyebrow: `INSPECTOR · ${m.subject.kind === 'call' ? 'call' : m.subject.kind === 'bit' ? 'bit' : 'object'}`,
    title: redactText(m.title, known),
    facts: m.facts.map(([k, v]) => [redactText(k, known), redactText(v, known)] as const),
    facets: m.facets.map((f) => ({ label: redactText(f.label, known), value: redactText(f.value, known), ...(f.caveat ? { caveat: redactText(f.caveat, known) } : {}) })),
    verbs: m.verbs.map((verb) => ({ label: verbWord(verb), verb })),
    tabs: [
      { key: 'rest', label: 'REST', text: texts?.rest ?? null },
      { key: 'curl', label: 'curl', text: texts ? (setup ? `${setup.curl}\n${texts.curl}` : texts.curl) : null },
      { key: 'mcp', label: 'MCP', text: texts ? (texts.mcp === null ? '(no MCP twin: this operation is x-mcp: false)' : setup ? `${setup.mcp}\n${texts.mcp}` : texts.mcp) : null },
      { key: 'answer', label: 'answer', text: m.answer ? answerText(m.answer, known, via) : m.subject.kind === 'call' ? '(at the door…)' : null },
    ],
    tone: answer === null ? (m.subject.kind === 'call' ? 'pending' : 'none') : answer.tone === 'info' ? 'none' : answer.tone,
  };
};

// -- a copy button that never copies a key --------------------------------------------------------

export const copyButton = (text: () => string, label = 'copy'): HTMLButtonElement => {
  const b = h('button', { class: 'insp-copy', type: 'button' }, label);
  b.addEventListener('click', () => {
    const value = redactText(text());
    void navigator.clipboard?.writeText(value).then(() => { b.textContent = 'copied'; setTimeout(() => { b.textContent = label; }, 1200); }, () => undefined);
  });
  return b;
};

/** REST · curl · MCP (· answer) as a tab strip over a <pre>; reused by the Deck's previews. */
export const twinTabs = (tabs: readonly { key: Tab; label: string; text: string | null }[], initial: Tab = 'rest'): HTMLElement => {
  const available = tabs.filter((t) => t.text !== null);
  let current: Tab = available.some((t) => t.key === initial) ? initial : (available[0]?.key ?? 'rest');
  const pre = h('pre', { class: 'insp-pre', tabindex: '0' });
  const strip = h('div', { class: 'insp-tabs', role: 'tablist' });
  const draw = (): void => {
    put(strip, ...available.map((t) => h('button', {
      class: 'insp-tab', type: 'button', role: 'tab', 'aria-selected': String(t.key === current),
      onclick: () => { current = t.key; draw(); },
    }, t.label)), copyButton(() => pre.textContent ?? ''));
    pre.textContent = available.find((t) => t.key === current)?.text ?? '';
  };
  draw();
  return h('div', { class: 'insp-twins' }, strip, pre);
};

// -- the screen -------------------------------------------------------------------------------------

/**
 * The Inspector's model of a subject: CORE's, over this tab's stores, spec, twins and verb ring.
 * A call that went as an MCP tools/call says the operation's own status, with the envelope's
 * kept second, as every panel does.
 */
export const modelFor = (ctx: Pick<AppContext, 'planes' | 'records' | 'catalogue' | 'client' | 'gestures'>, subject: Subject): InspectorModel => {
  const m = inspect(subject, {
    planes: ctx.planes, records: ctx.records, catalogue: ctx.catalogue(),
    twins: (op, args, persona) => ctx.client.twins(op, args, persona),
    ring: (s) => { try { return ctx.gestures.ring(s); } catch { return []; } },
  });
  const a = subject.kind === 'call' ? subject.record.answer : null;
  if (subject.kind !== 'call' || !a) return m;
  const v = answerOf(a, subject.record.via);
  if (v.mcp === null && !v.rpc) return m;
  const status = v.rpc ? v.detail : `${v.status} (via MCP ${v.mcp})`;
  return { ...m, facts: m.facts.map(([k, x]) => (k === 'status' ? [k, status] as const : [k, x] as const)) };
};

export const mount: Screen = (root, ctx) => {
  let pinned: InspectorModel | null = null;
  let follow = true;
  let latest: InspectorModel | null = null;
  /** The thing looked at before the latest call took its place. */
  let passed: InspectorModel | null = null;
  const offs: Unsub[] = [];
  const body = h('div', { class: 'insp-body' });
  const followBox = h('input', { type: 'checkbox', checked: true });
  const draw = (): void => {
    const m = pinned ?? (follow ? latest : null);
    if (!m) {
      put(body, h('p', { class: 'insp-empty' }, 'Pick up anything, or make a call: its facts, verbs and the call as REST, curl and MCP appear here.'));
      return;
    }
    const v = viewOf(m, knownSecrets(ctx), { apiBase: ctx.session.apiBase });
    // Following calls, the thing last looked at stays one click away.
    const back = !pinned && passed ? passed : null;
    put(body,
      h('header', { class: `insp-head t-${v.tone}` },
        h('span', { class: 'insp-eyebrow' }, v.eyebrow),
        h('h2', { class: 'insp-title' }, v.title),
        pinned ? h('button', { class: 'insp-unpin', type: 'button', onclick: () => { pinned = null; draw(); } }, 'unpin') : null,
        back ? h('button', { class: 'insp-unpin', type: 'button', onclick: () => { pinned = back; passed = null; draw(); } }, `↩ ${back.title}`) : null),
      v.facts.length ? h('dl', { class: 'insp-facts' }, ...v.facts.flatMap(([k, val]) => [h('dt', {}, k), h('dd', {}, val)])) : null,
      v.facets.length ? h('ul', { class: 'insp-facets' }, ...v.facets.map((f) => h('li', {},
        h('span', { class: 'insp-facet' }, `${f.label} ${f.value}`),
        f.caveat ? h('span', { class: 'insp-caveat' }, `⚠ ${f.caveat}`) : null))) : null,
      v.verbs.length ? h('div', { class: 'insp-verbs' }, h('span', { class: 'insp-label' }, 'VERBS'),
        ...v.verbs.map(({ label, verb }) => h('button', {
          class: 'insp-verb', type: 'button', title: `${verb.id} · ${verb.op}`,
          onclick: () => { if (m.subject.kind === 'noun') ctx.gestures.lift(m.subject.held, 'click'); },
        }, label))) : null,
      v.tabs.some((t) => t.text !== null) ? twinTabs(v.tabs, v.tone === 'refused' || v.tone === 'error' ? 'answer' : 'rest') : null);
  };
  offs.push(ctx.bus.on('inspect', ({ subject }) => {
    pinned = modelFor(ctx, subject);
    draw();
    return true;
  }));
  offs.push(ctx.client.on((record, phase) => {
    if (record.source.kind === 'auto') return;
    latest = modelFor(ctx, { kind: 'call', record });
    if (pinned && pinned.subject.kind === 'call' && pinned.subject.record.id === record.id) pinned = latest;
    // Following the latest call, a new call takes the place of a thing looked at (a bit, an orb); a call pinned stays.
    if (follow && pinned && pinned.subject.kind !== 'call') { passed = pinned; pinned = null; }
    if (phase === 'answered' || !pinned) draw();
  }));
  followBox.addEventListener('change', () => { follow = followBox.checked; draw(); });
  put(root, h('section', { class: 'insp', 'aria-label': 'Inspector' },
    h('div', { class: 'insp-bar' }, h('label', { class: 'insp-follow' }, followBox, ' follow the latest call')),
    body));
  draw();
  return () => {
    offs.forEach((off) => off());
    root.replaceChildren();
  };
};

